import { Box, Button, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  convertDateTimeSimple,
  toastInfo,
  toastSuccess,
  toUSDFormat,
} from "../../utils/utils";
import useStyles from "./index.styles";

import BustOutPopup from "../../components/InJailPopup/BustOutPopup";
import BuyOutPopup from "../../components/InJailPopup/BuyOutPopup";
import CrimeCaughtPopup from "../../components/JailBustOutPopup/CrimeCaughtPopup";
import CrimeFailedPopup from "../../components/JailBustOutPopup/CrimeFailedPopup";
import CrimeSuccessPopup from "../../components/JailBustOutPopup/CrimeSuccessPopup";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import config from "../../config/config";
import { City, CONFIRMATION_DELAY } from "../../constants/const";
import { BribePerMin, CrimeFailedReason } from "../../constants/jail";
import { Messages } from "../../constants/messages";
import { RankXP } from "../../constants/rank";
import { getGameBankAllowance } from "../../helper/contractFunctions/MafiaGameBank";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import {
  approveGameCashAction,
  bustOutAction,
  buyOutAction,
  getNextBustOutTimeAction,
} from "../../reducers/game.slice";
import { getJailList } from "../../reducers/jail.slice";
import { getMyProfile } from "../../reducers/profile.slice";
import { JailListType } from "../../types/Jail/JailListType";

import { toast } from "react-toastify";
import useJailSSE from "./hooks/useJailSSE";

const Jail = () => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const { account } = useWallet();
  const currentTime = useCurrentTime();

  const { jailedUntil, nextBustOutTime } = useAppSelector(
    (state) => state.game
  );
  const { jailList } = useAppSelector((state) => state.jail);
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { profileNames, myProfile } = useAppSelector((state) => state.profile);

  const { updateBalance, balance } = useGameBankBalance(
    account,
    signMsg,
    signature
  );

  const [openBustOutModal, setOpenBustOutModal] = useState(false);
  const [openBuyoutModal, setOpenBuyOutModal] = useState(false);
  const [openBustOutSuccessModal, setOpenBustOutSuccessModal] = useState(false);
  const [openBustOutFailedModal, setOpenBustOutFailedModal] = useState(false);
  const [openBustOutCaughtModal, setOpenBustOutCaughtModal] = useState(false);

  const [successXpAmount, setSuccessXpAmount] = useState(0);
  const [successXpPercent, setSuccessXpPercent] = useState(0);

  const filteredJailList = jailList.filter(
    (item) => item.jailedUntil > currentTime
  );

  const calculateBribeAmount = (jailedUntil: number) => {
    const jailTime = Math.max(0, jailedUntil - currentTime);
    // Convert to BigNumber for precise calculation
    const timeInSeconds = jailTime;
    const price = (timeInSeconds * BribePerMin) / 60;

    // Convert to readable format
    return price;
  };

  const onBustSuccess = useCallback(
    (xpAmount: number) => {
      setOpenBustOutSuccessModal(true);
      setSuccessXpAmount(successXpAmount);
      dispatch(getNextBustOutTimeAction(account));
      toastSuccess(Messages.JAIL.BUST_OUT.SUCCESS, { playerName: "a player" });
      if (myProfile.rankNumber && myProfile.rankNumber < RankXP.length) {
        const xpPercent =
          (xpAmount /
            (RankXP[myProfile.rankNumber] - RankXP[myProfile.rankNumber - 1])) *
          100;

        setSuccessXpPercent(xpPercent);
      }
      myProfile.name && dispatch(getMyProfile(myProfile.name));
    },
    [account, dispatch, myProfile.name, myProfile.rankNumber, successXpAmount]
  );

  const onBustCaught = useCallback(() => {
    setOpenBustOutCaughtModal(true);
    dispatch(getNextBustOutTimeAction(account));
  }, [account, dispatch]);

  const onBustFailed = useCallback(() => {
    setOpenBustOutFailedModal(true);
    dispatch(getNextBustOutTimeAction(account));
  }, [account, dispatch]);

  const handleBuyOut = async (amount: number, name: string) => {
    const profile = profileNames.find((profile) => profile.name === name);
    if (!profile || !profile.address) return;

    if (balance < amount) {
      toast.info("Insufficient cash balance");
      return;
    }

    const allowance = await getGameBankAllowance(account, config.jailAddress);

    const buyOut = () => {
      dispatchTxAction(
        dispatch,
        buyOutAction({ address: profile.address || "", account }),
        () => {
          toastSuccess(Messages.JAIL.BUY_OUT.SUCCESS);
          updateBalance(account, signMsg, signature);
        },
        CONFIRMATION_DELAY
      );
    };

    if (allowance < amount) {
      dispatchTxAction(
        dispatch,
        approveGameCashAction({
          account,
          spender: config.jailAddress,
          cashAmount: 10 ** 18,
        }),
        () => {
          buyOut();
        },
        CONFIRMATION_DELAY
      );
    } else {
      buyOut();
    }
  };

  const handleBustOut = (name: string) => {
    const profile = profileNames.find((profile) => profile.name === name);
    if (!profile || !profile.address) return;

    if (currentTime < nextBustOutTime) {
      toastInfo(Messages.JAIL.BUST_OUT.WAIT_COOLDOWN);
      return;
    }

    dispatchTxAction(
      dispatch,
      bustOutAction({ address: profile.address || "", account }),
      () => {},
      CONFIRMATION_DELAY
    );
  };

  useJailSSE(account, onBustSuccess, onBustCaught, onBustFailed);

  useEffect(() => {
    dispatch(getJailList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getNextBustOutTimeAction(account));
  }, [account, dispatch]);

  return (
    <Box className={classes.body}>
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <Typography
              fontFamily={"Philosopher"}
              color={"white"}
              textAlign={"center"}
              fontSize={36}
              fontWeight={700}
            >
              Jail
            </Typography>

            <Box
              component={"img"}
              src={"/assets/imgs/jail/handcuffs.png"}
              alt="Jail Image"
            />
          </Box>

          <Box className={classes.headerRight}>
            <Box className={classes.headerInfoItem}>
              <Box
                component={"img"}
                src={"/assets/imgs/jail/prisoner.png"}
                alt="Prisoner"
              />
              <Typography
                fontFamily={"Philosopher"}
                color={"grey"}
                textAlign={"center"}
                fontSize={16}
                fontWeight={700}
              >
                {filteredJailList.length} mobsters serving time
              </Typography>
            </Box>

            <Box className={classes.headerInfoItem}>
              <Box
                component={"img"}
                src={"/assets/imgs/travel/clockwise.png"}
                alt="Prisoner"
              />

              <Typography
                fontFamily={"Philosopher"}
                color={"grey"}
                textAlign={"center"}
                fontSize={16}
                fontWeight={700}
              >
                Next bust out try in:{" "}
                {nextBustOutTime > currentTime
                  ? convertDateTimeSimple(nextBustOutTime - currentTime)
                  : "Now"}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className={classes.prisonerListPanel}>
          <Box className={classes.prisonerListHeader}>
            <Typography>Username</Typography>
            <Typography>Family</Typography>
            <Typography>Crime</Typography>
            <Typography>Location</Typography>
            <Typography>Time left</Typography>
            <Typography>Buy out</Typography>
            <Typography>Bust out</Typography>
          </Box>

          {filteredJailList.length === 0 ? (
            <Box className={classes.noUserDes}>There is no jailed user.</Box>
          ) : (
            filteredJailList.map((item: JailListType, index) => (
              <Box className={classes.prisonerListItem} key={index}>
                <Typography component={Link} to={`/profile/${item.name}`}>
                  {item.name}
                </Typography>
                <Typography>{item.userFamily?.name}</Typography>
                <Typography>{CrimeFailedReason[item.crimeType]}</Typography>
                <Typography>{City[item.city]}</Typography>
                <Typography>
                  {convertDateTimeSimple(
                    Math.max(item.jailedUntil - currentTime, 0)
                  )}
                </Typography>

                <Button
                  variant="contained"
                  className={classes.moneyCopyButton}
                  startIcon={
                    <img src="/assets/imgs/layout/currency.png" alt="Money" />
                  }
                  onClick={() =>
                    handleBuyOut(
                      calculateBribeAmount(item.jailedUntil),
                      item.name
                    )
                  }
                >
                  {toUSDFormat(
                    Math.ceil(calculateBribeAmount(item.jailedUntil))
                  )}
                </Button>

                {item.name !== myProfile.name && jailedUntil < currentTime && (
                  <Button
                    variant="contained"
                    className={classes.bustOutButton}
                    onClick={() => handleBustOut(item.name)}
                  >
                    Bust out
                  </Button>
                )}
              </Box>
            ))
          )}
        </Box>
      </Box>

      <BustOutPopup
        openModal={openBustOutModal}
        setOpenModal={setOpenBustOutModal}
      />

      <BuyOutPopup
        openModal={openBuyoutModal}
        setOpenModal={setOpenBuyOutModal}
      />

      <CrimeSuccessPopup
        openModal={openBustOutSuccessModal}
        setOpenModal={setOpenBustOutSuccessModal}
        successXpPercent={successXpPercent}
      />

      <CrimeFailedPopup
        openModal={openBustOutFailedModal}
        setOpenModal={setOpenBustOutFailedModal}
      />

      <CrimeCaughtPopup
        openModal={openBustOutCaughtModal}
        setOpenModal={setOpenBustOutCaughtModal}
      />
    </Box>
  );
};

export default Jail;
